import {
  DEFAULT_SURFACE_NORMAL,
  coneAround,
  deriveImpactFrame,
  type ImpactFrame,
} from './impactFrame';
import type { CloudSpawnRecord } from './cloudSpawn';
import type { ParticleMaterialProfile } from './profiles';
import { clampEnergy, energyJointScale } from '../../services/energy';
import { impactJointScale, resolveImpact, type ImpactParams } from './impactRegime';
import { mulberry32, seedFromBurst } from './spawn';

export const JOINT_KIND_PUFF = 4;
export const JOINT_KIND_BLEED = 5;

export interface JointPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly courseIndex: number;
  readonly stretchIndex: number;
}

export interface ScheduledFine {
  readonly emitAt: number;
  readonly role: 'puff' | 'bleed';
  readonly record: CloudSpawnRecord;
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function varyTint(
  base: readonly [number, number, number],
  rng: () => number,
  amp: number,
): [number, number, number] {
  const j = amp * (rng() * 2 - 1);
  return [
    clamp01(base[0] + j),
    clamp01(base[1] + j * 0.45),
    clamp01(base[2] + j * 0.3),
  ];
}

/**
 * More-vertical axis = course (brick rows). More-horizontal = stretcher.
 */
export function jointAxes(frame: ImpactFrame): {
  course: readonly [number, number, number];
  stretch: readonly [number, number, number];
} {
  const tUp = Math.abs(frame.tangent[1]);
  const bUp = Math.abs(frame.bitangent[1]);
  if (bUp >= tUp) return { course: frame.bitangent, stretch: frame.tangent };
  return { course: frame.tangent, stretch: frame.bitangent };
}

export function snapToBrickJoint(
  point: readonly [number, number, number],
  origin: readonly [number, number, number],
  courseAxis: readonly [number, number, number],
  stretchAxis: readonly [number, number, number],
  course: number,
  stretcher: number,
): { pos: [number, number, number]; courseIndex: number; stretchIndex: number } {
  const cH = Math.max(1e-6, course);
  const sW = Math.max(1e-6, stretcher);
  const relX = point[0] - origin[0];
  const relY = point[1] - origin[1];
  const relZ = point[2] - origin[2];
  const cy = relX * courseAxis[0] + relY * courseAxis[1] + relZ * courseAxis[2];
  const sx = relX * stretchAxis[0] + relY * stretchAxis[1] + relZ * stretchAxis[2];
  const courseIndex = Math.round(cy / cH);
  const odd = (courseIndex & 1) === 1;
  const sxAdj = sx - (odd ? sW * 0.5 : 0);
  const stretchIndex = Math.round(sxAdj / sW);
  const sxSnap = stretchIndex * sW + (odd ? sW * 0.5 : 0);
  const cySnap = courseIndex * cH;
  return {
    pos: [
      origin[0] + courseAxis[0] * cySnap + stretchAxis[0] * sxSnap,
      origin[1] + courseAxis[1] * cySnap + stretchAxis[1] * sxSnap,
      origin[2] + courseAxis[2] * cySnap + stretchAxis[2] * sxSnap,
    ],
    courseIndex,
    stretchIndex,
  };
}

/**
 * Stair-step joint positions on the wall plane, quantized to a virtual
 * running-bond brick grid around the crater.
 */
export function quantizeJointPoints(
  crater: readonly [number, number, number],
  frame: ImpactFrame,
  count: number,
  course: number,
  stretcher: number,
  rng: () => number,
): JointPoint[] {
  const n = Math.max(0, count | 0);
  if (n === 0) return [];
  const { course: courseAxis, stretch: stretchAxis } = jointAxes(frame);
  const seen = new Set<string>();
  const out: JointPoint[] = [];
  let guard = 0;
  while (out.length < n && guard < n * 12) {
    guard += 1;
    const dc = (rng() * 2 - 1) * 3.2 * course;
    const ds = (rng() * 2 - 1) * 2.2 * stretcher;
    const raw: [number, number, number] = [
      crater[0] + courseAxis[0] * dc + stretchAxis[0] * ds,
      crater[1] + courseAxis[1] * dc + stretchAxis[1] * ds,
      crater[2] + courseAxis[2] * dc + stretchAxis[2] * ds,
    ];
    const snapped = snapToBrickJoint(raw, crater, courseAxis, stretchAxis, course, stretcher);
    const key = `${snapped.courseIndex}:${snapped.stretchIndex}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const lift = 0.018;
    out.push({
      x: snapped.pos[0] + frame.normal[0] * lift,
      y: snapped.pos[1] + frame.normal[1] * lift,
      z: snapped.pos[2] + frame.normal[2] * lift,
      courseIndex: snapped.courseIndex,
      stretchIndex: snapped.stretchIndex,
    });
  }
  return out;
}

function makeScheduled(
  role: 'puff' | 'bleed',
  emitAt: number,
  px: number,
  py: number,
  pz: number,
  vx: number,
  vy: number,
  vz: number,
  size: number,
  linger: number,
  tint: readonly [number, number, number],
  seed: number,
  depositMass: number,
  origin: readonly [number, number, number],
  energy: number,
  frame: ImpactFrame,
  buoyancy: number,
): ScheduledFine {
  return {
    emitAt,
    role,
    record: {
      x: px,
      y: py,
      z: pz,
      vx,
      vy,
      vz,
      size,
      seed,
      kind: role === 'bleed' ? JOINT_KIND_BLEED : JOINT_KIND_PUFF,
      depositMass,
      ox: origin[0],
      oy: origin[1],
      oz: origin[2],
      energy,
      r: tint[0],
      g: tint[1],
      b: tint[2],
      linger,
      buoyancy,
      nx: frame.normal[0],
      ny: frame.normal[1],
      nz: frame.normal[2],
      upFactor: frame.upFactor,
    },
  };
}

export interface JointPlanInput {
  readonly position: readonly [number, number, number];
  readonly energy: number;
  readonly profile: ParticleMaterialProfile;
  readonly burstIndex: number;
  readonly surfaceNormal?: readonly [number, number, number];
  readonly fineMassBudget?: number;
  readonly impact?: ImpactParams;
}

/**
 * Schedule grey joint puffs (0.2–0.6 s) and later bleed ribbons (2–3.5 s)
 * from stair-step-quantized points. No-op when the profile has no joint grid
 * or the surface is a floor (upFactor ≈ 1).
 */
export function planJointSchedule(input: JointPlanInput): {
  points: JointPoint[];
  fines: ScheduledFine[];
} {
  const sig = input.profile.signature;
  const frame = deriveImpactFrame(input.surfaceNormal ?? DEFAULT_SURFACE_NORMAL);
  const [p0, p1] = sig.jointPointRange;
  if (p1 <= 0 || frame.upFactor > 0.55) return { points: [], fines: [] };

  const jointScale = energyJointScale(input.energy) * impactJointScale(resolveImpact(input.impact));
  if (jointScale <= 0) return { points: [], fines: [] };

  const rng = mulberry32(seedFromBurst(input.burstIndex) ^ 0x4a1b7eed);
  const nPts = Math.max(0, Math.round((p0 + rng() * Math.max(0, p1 - p0)) * jointScale));
  const points = quantizeJointPoints(
    input.position,
    frame,
    nPts,
    sig.jointCourse,
    sig.jointStretcher,
    rng,
  );
  if (points.length === 0) return { points, fines: [] };

  const mortar = input.profile.cloudTints[1] ?? input.profile.cloudTints[0];
  const energy = clampEnergy(input.energy);
  const down: [number, number, number] = [
    -frame.normal[0] * 0.04,
    -1,
    -frame.normal[2] * 0.04,
  ];
  const slide = [
    down[0] - frame.normal[0] * (down[0] * frame.normal[0] + down[1] * frame.normal[1] + down[2] * frame.normal[2]),
    down[1] - frame.normal[1] * (down[0] * frame.normal[0] + down[1] * frame.normal[1] + down[2] * frame.normal[2]),
    down[2] - frame.normal[2] * (down[0] * frame.normal[0] + down[1] * frame.normal[1] + down[2] * frame.normal[2]),
  ];
  const sLen = Math.hypot(slide[0], slide[1], slide[2]) || 1;
  slide[0] /= sLen;
  slide[1] /= sLen;
  slide[2] /= sLen;

  const fines: ScheduledFine[] = [];
  const [pu0, pu1] = sig.jointPuffWindow;
  const [pp0, pp1] = sig.jointPuffPerPoint;
  const puffBudget = Math.max(0, input.fineMassBudget ?? 0);
  const puffCounts = points.map(() => pp0 + Math.round(rng() * Math.max(0, pp1 - pp0)));
  let puffSlots = 0;
  for (let i = 0; i < puffCounts.length; i += 1) puffSlots += puffCounts[i];
  const puffEach = puffSlots > 0 && puffBudget > 0 ? (puffBudget * 0.45) / puffSlots : 0;

  for (let pi = 0; pi < points.length; pi += 1) {
    const pt = points[pi];
    const nPuff = puffCounts[pi];
    for (let i = 0; i < nPuff; i += 1) {
      const emitAt = pu0 + rng() * Math.max(0, pu1 - pu0);
      const tint = varyTint(mortar, rng, 0.03);
      const kick = coneAround(rng, frame.normal, 0.55);
      const speed = 0.18 + rng() * 0.28;
      fines.push(makeScheduled(
        'puff',
        emitAt,
        pt.x + (rng() * 2 - 1) * 0.03,
        pt.y + (rng() * 2 - 1) * 0.02,
        pt.z + (rng() * 2 - 1) * 0.03,
        kick[0] * speed + slide[0] * 0.08,
        kick[1] * speed + slide[1] * 0.08,
        kick[2] * speed + slide[2] * 0.08,
        0.11 + rng() * 0.1,
        0.85 + rng() * 0.55,
        tint,
        rng(),
        puffEach,
        input.position,
        energy,
        frame,
        0.35,
      ));
    }
  }

  const [b0, b1] = sig.jointBleedPointRange;
  const bleedN = Math.min(points.length, b0 + Math.round(rng() * Math.max(0, b1 - b0)));
  const bleedPts = points.slice();
  for (let i = bleedPts.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = bleedPts[i];
    bleedPts[i] = bleedPts[j];
    bleedPts[j] = tmp;
  }
  const chosen = bleedPts.slice(0, bleedN);
  const [bw0, bw1] = sig.jointBleedWindow;
  const [bp0, bp1] = sig.jointBleedPerPoint;
  const bleedCounts = chosen.map(() => bp0 + Math.round(rng() * Math.max(0, bp1 - bp0)));
  let bleedSlots = 0;
  for (let i = 0; i < bleedCounts.length; i += 1) bleedSlots += bleedCounts[i];
  const bleedEach = bleedSlots > 0 && puffBudget > 0 ? (puffBudget * 0.55) / bleedSlots : 0;

  for (let bi = 0; bi < chosen.length; bi += 1) {
    const pt = chosen[bi];
    const nBleed = bleedCounts[bi];
    for (let i = 0; i < nBleed; i += 1) {
      const emitAt = bw0 + rng() * Math.max(0, bw1 - bw0);
      const tint = varyTint(mortar, rng, 0.025);
      const speed = 0.35 + rng() * 0.45;
      const out = 0.04 + rng() * 0.06;
      fines.push(makeScheduled(
        'bleed',
        emitAt,
        pt.x + (rng() * 2 - 1) * 0.015,
        pt.y + (rng() * 2 - 1) * 0.01,
        pt.z + (rng() * 2 - 1) * 0.015,
        slide[0] * speed + frame.normal[0] * out,
        slide[1] * speed + frame.normal[1] * out,
        slide[2] * speed + frame.normal[2] * out,
        0.055 + rng() * 0.05,
        1.15 + rng() * 0.7,
        tint,
        rng(),
        bleedEach,
        input.position,
        energy,
        frame,
        0.12,
      ));
    }
  }

  return { points, fines };
}
